import { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import {
  Button,
  Heading,
  Loader,
  FormField,
  Input,
  Form,
} from '@livechat/design-system-react-components';
import AccountsSDK from '@livechat/accounts-sdk';

import Config from 'Config';
import { ErrorMessageForm } from 'components/forms/error-message-form/ErrorMessageForm';
import { EmailInUseError } from 'components/error-message/EmailInUseError';
import { JoinLinkExpired } from 'components/error-message/JoinLinkExpired';
import { fetchMe } from 'hooks/use-get-me';
import { anyToBoolean } from 'helpers/boolean';
import { fetchTokenDetails } from 'hooks/use-get-token-details';
import Location from 'services/Location';
import { Product } from 'types/product';
import { Layout } from 'components/layout/Layout';
import { PasswordRequirements } from 'components/password-requirements/PasswordRequirements';
import { useSharedStore } from 'stores/shared-store';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { submitPost } from 'helpers/form';
import Amplitude from 'services/Amplitude';
import { validateName } from 'helpers/validators/name';
import { validateEmail } from 'helpers/validators/email';
import { validatePassword } from 'helpers/validators/password';
import { FormStates, SignupFormData } from 'types/form';
import SignupLegalInfo from 'routes/signup/components/SignupLegalInfo';
import { livechatClient } from 'services/connectivity/livechat/client';
import { CreateAgentRequest } from 'services/connectivity/livechat/types';

import { AlreadySignedIn } from './already-signed-in/AlreadySignedIn';

import * as styles from './styles';

export const Join: FC = () => {
  const sharedStore = useSharedStore(['email', 'name', 'password', 'autoSubmit']);

  const [isTokenValid, setIsTokenValid] = useState<boolean>(true);
  const [isProcessingToken, setIsProcessingToken] = useState<boolean>(true);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [nameValidationError, setNameValidationError] = useState<string | null>(null);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [formState, setFormState] = useState<string>(FormStates.Default);
  const [formData, setFormData] = useState<SignupFormData>(
    sharedStore.multiGet(['email', 'password', 'name'])
  );

  const accessToken = Location.getUrlParam('access_token');
  const autoSubmit = anyToBoolean(sharedStore.get('autoSubmit'));

  const handleNameInputChange = (e) => {
    if (e.target.value.trim() !== '') {
      setNameValidationError(null);
    }

    setFormData((prevState) => ({
      ...prevState,
      name: e.target.value,
    }));
  };

  const handleEmailInputChange = (e) => {
    if (!validateEmail(e.target.value.trim())) {
      setEmailValidationError(null);
    }

    setFormData((prevState) => ({
      ...prevState,
      email: e.target.value,
    }));
  };

  const handlePasswordInputChange = (e) => {
    if (!validatePassword(e.target.value.trim())) {
      setPasswordValidationError(null);
    }

    setFormData((prevState) => ({
      ...prevState,
      password: e.target.value,
    }));
  };

  const handleJoinButton = () => {
    const nameValidation = validateName(formData.name);
    const emailValidation = validateEmail(formData.email);
    const passwordValidation = validatePassword(formData.password);

    if (nameValidation) {
      setNameValidationError(nameValidation);
    }

    if (emailValidation) {
      setEmailValidationError(emailValidation);
    }

    if (passwordValidation) {
      setPasswordValidationError(passwordValidation);
    }
  };

  const handleSubmit = async (e?) => {
    if (e) {
      e.preventDefault();
    }

    if (nameValidationError || emailValidationError || passwordValidationError) {
      return;
    }

    setFormState(FormStates.Requesting);

    const data: CreateAgentRequest = {
      id: formData.email,
      name: formData.name,
      password: formData.password,
    };

    const { error } = await livechatClient.createAgent(data, accessToken);

    if (error) {
      if (error.error.type !== 'already_exists') {
        Amplitude.logEvent(formData.email, 'Agents: Join from expired link attempt');
      }

      setRequestError(error.error.type);
      return;
    }

    submitPost(`${Config.accounts_api_url}/?${buildOauthQuery(Config)}`, {
      email: formData.email,
      password: formData.password,
    });

    Cookies.set('product', Product.LiveChat, { expires: 365, sameSite: 'Lax' });
    Amplitude.logEvent(formData.email, 'Agents: Join from link succeeded');
  };

  const handleResetError = (e) => {
    e.preventDefault();
    setRequestError(null);
    setFormState(FormStates.Default);
  };

  useEffect(() => {
    if (autoSubmit) {
      handleSubmit();
    }

    const checkTokenAndGetAccount = async () => {
      let meResponse = null;
      let tokenDetails = null;

      try {
        const sdk = new AccountsSDK({
          client_id: Config.organization_app_client_id,
          server_url: Config.accounts_url,
        });
        const response = await sdk.iframe().authorize();
        meResponse = await fetchMe(response.access_token);
      } catch (e: any) {
        // Ignore error agent not authorized;
      }

      Amplitude.logEvent(meResponse?.email, 'Agents: Join from link started');

      try {
        tokenDetails = await fetchTokenDetails(accessToken);
      } catch (error) {
        Amplitude.logEvent(meResponse?.email, 'Agents: Join from expired link attempt');
      }

      setIsProcessingToken(false);
      setIsTokenValid(!!tokenDetails);
      setAccountName(meResponse?.name);
    };

    checkTokenAndGetAccount();
  }, []);

  if (requestError) {
    switch (requestError) {
      case 'already_exists':
        return <EmailInUseError onClick={handleResetError} />;
      default:
        return (
          <ErrorMessageForm
            titleCaption="The link is incorrect"
            titleContent={
              <>
                The link you provided doesn’t seem to work.
                <br /> Please contact the inviting person to get a new one.
              </>
            }
            contactSupportButton
            backToLogin
          />
        );
    }
  }

  if (formState === FormStates.Requesting) {
    return (
      <div css={styles.submitingContainer} data-testid="join-loader-container">
        <Heading size="lg">Creating account...</Heading>
        <Loader />
      </div>
    );
  }

  if (isProcessingToken) {
    return (
      <div css={styles.loaderContainer} data-testid="join-loader-container">
        <Loader />
      </div>
    );
  }

  if (accountName) {
    return (
      <Layout showHeaderLogo showNewAccountAction>
        <AlreadySignedIn user={accountName} />
      </Layout>
    );
  }

  if (!isTokenValid) {
    return <JoinLinkExpired name={accountName} />;
  }

  return (
    <Layout showHeaderLogo showAlreadyHaveAnAccount>
      <div css={styles.container}>
        <Heading size="lg">Join your team on LiveChat</Heading>
        <Form onSubmit={handleSubmit}>
          <FormField
            labelText="Full name"
            labelFor="name"
            css={styles.formField}
            error={nameValidationError}
          >
            <Input
              type="name"
              name="name"
              placeholder="John Smith"
              value={formData.name}
              inputSize="large"
              onChange={handleNameInputChange}
              error={!!nameValidationError}
              autoFocus
            />
          </FormField>

          <FormField
            labelText="Business email"
            labelFor="email"
            css={styles.formField}
            error={emailValidationError}
          >
            <Input
              type="email"
              name="email"
              placeholder="name@work-email.com"
              value={formData.email}
              inputSize="large"
              onChange={handleEmailInputChange}
              error={!!emailValidationError}
            />
          </FormField>

          <FormField
            labelText="Password"
            labelFor="password"
            css={styles.passwordField}
            error={passwordValidationError}
          >
            <Input
              type="password"
              name="password"
              placeholder="Set your password"
              value={formData.password}
              inputSize="large"
              onChange={handlePasswordInputChange}
              error={!!passwordValidationError}
            />
          </FormField>
          <PasswordRequirements
            password={formData.password}
            showErrors={!!passwordValidationError}
          />

          <div>
            <Button
              type="submit"
              kind="destructive"
              size="large"
              onClick={handleJoinButton}
              loading={formState === FormStates.Requesting}
              css={styles.button}
            >
              Join your team
            </Button>
            <SignupLegalInfo />
          </div>
        </Form>
      </div>
    </Layout>
  );
};
